import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { PageQuery, PageResp } from "./page";
import type { JsonObject } from "./common";
import type { EpisodeStatus } from "@/modules/dataset/public";
import type { EpisodeValidationStatus } from "./datasetEpisode";

export type CleanupTargetStatus = "cancelled" | "archived";
export type StorageCleanupTarget = {
  dataset_uuid: string;
  display_name: string;
  status: CleanupTargetStatus;
  object_key_prefix: string;
  freed_bytes_estimate: number;
  retention_elapsed: boolean;
  shared_anchor: boolean;
  training_registered: boolean;
  eligible: boolean;
  job_status: "queued" | "running" | "succeeded" | "failed" | null;
  job_error: JsonObject | null;
};
export type StorageCleanupRequest = {
  dataset_uuids: readonly string[];
  statuses?: readonly CleanupTargetStatus[];
};
export type StorageCleanupPreview = {
  entries: StorageCleanupTarget[];
  errors: { dataset_uuid: string; reason: string }[];
  freed_bytes: number;
  total: number;
};
export async function listStorageCleanupTargets(
  query: PageQuery & { status?: CleanupTargetStatus },
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<StorageCleanupTarget>> {
  return (
    await apiClient.get<PageResp<StorageCleanupTarget>>(apiRoutes.admin.storageCleanupTargets, {
      params: query,
      signal: options.signal,
    })
  ).data;
}
// 관리자 UI는 명시적으로 선택한 범위만 실행한다.
// 빈 선택을 서버가 전체 범위로 해석하지 않게, 이 경계에서 빈 선택을 막는다.
export async function runStorageCleanup(
  action: "preview" | "execute",
  request: StorageCleanupRequest,
): Promise<StorageCleanupPreview> {
  if (request.dataset_uuids.length === 0 || request.dataset_uuids.length > 500)
    throw new Error("회수 대상을 1~500개 선택해 주세요.");
  const route =
    action === "preview"
      ? apiRoutes.admin.storageCleanupDryRun
      : apiRoutes.admin.storageCleanupExecute;
  return (await apiClient.post<StorageCleanupPreview>(route, request)).data;
}
// BE `EpisodeInvalidReason` 과 같은 값이다.
export type { EpisodeInvalidReason } from "./episodeReviewState";
import type { EpisodeInvalidReason } from "./episodeReviewState";
// BE `EpisodeInvalidCategory` 와 같은 값이다. 사유가 분류를 정한다.
export type EpisodeInvalidCategory = "file_defect" | "content_defect" | "unverified";
// 관리자 invalid 목록의 상태다. `invalid` 는 검증이 invalid 인 에피소드이고, 결함을 수용한 것도
// 담는다. `accepted` 는 결함을 수용한 에피소드이고, 지금 검증 상태와 관계없다. 두 목록은 겹친다.
// 수용은 검증 상태를 바꾸지 않는다.
export type InvalidEpisodeState = "invalid" | "accepted";
export type InvalidEpisode = {
  uuid: string;
  name: string;
  validation_status: EpisodeValidationStatus;
  invalid_reason: EpisodeInvalidReason | null;
  invalid_category: EpisodeInvalidCategory | null;
  // 결함 수용 여부다. 수용은 리뷰 쓰기(`PUT …/episodes/{uuid}/review`)만 바꾼다.
  accepted_invalid: boolean;
  // 에피소드의 표시 상태다. `excluded` 이면 재검사가 409 `episode.excluded` 다. 리뷰에서 먼저 복원한다.
  status: EpisodeStatus;
  datasets: { uuid: string; display_name: string }[];
};
export type ClearInvalidEpisodeResp = {
  uuid: string;
  validation_status: EpisodeValidationStatus;
  accepted_invalid: boolean;
};
export async function listInvalidEpisodes(
  query: PageQuery & { state?: InvalidEpisodeState },
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<InvalidEpisode>> {
  return (
    await apiClient.get<PageResp<InvalidEpisode>>(apiRoutes.admin.invalidEpisodes, {
      params: query,
      signal: options.signal,
    })
  ).data;
}
/**
 * invalid 판정을 재검사 대기로 되돌린다. 검증 상태는 pending 이 되고 사유는 지워진다.
 *
 * 결함 수용·스냅샷·코멘트·제외 여부는 바꾸지 않는다. 재진단 잡도 만들지 않는다. 운영자가 재진단을
 * 따로 요청한다. 해제 방식은 `recheck` 하나뿐이다. 수용은 이 경로로 바꾸지 않는다.
 * invalid 도 아니고 수용도 안 한 에피소드면 409 `episode.not_invalid` 다.
 */
export async function clearEpisodeInvalid(uuid: string): Promise<ClearInvalidEpisodeResp> {
  return (
    await apiClient.post<ClearInvalidEpisodeResp>(apiRoutes.admin.clearEpisodeInvalid(uuid), {
      mode: "recheck",
    })
  ).data;
}
